/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React, { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button } from 'reactstrap';
import { UtrvStatus } from '../../constants/status';
import { useAppSelector } from '../../reducers';
import { generateUrl } from '../../routes/util';
import G17Client from '../../services/G17Client';
import { CustomInitiatives, CustomReportType, NewInitiativesOrSurvey } from '../../types/custom-report';
import { BasicAlert } from '../alert/BasicAlert';
import { SubmitButton } from '../button/SubmitButton';
import { FieldProps, FormGenerator } from '../form/FormGenerator';
import Loader from '../loader';
import LoadingPlaceholder from '../LoaderContainer/LoadingPlaceholder';
import { CustomReportRoutes } from './CustomReportsRoot';
import { SubsidiarySelectionTable } from './partials/SubsidiarySelectionTable';
import { SurveySelectionTable, TableNodeWithSurveyIds } from './partials/SurveySelectionTable';
import './styles.scss';
import { convertToUtrvStatuses, DownloadUtrvStatusCombined, FormData, getInitialState } from './util/downloadReportHandler';
import { getSelectedSurveyNodeIds } from './util/treeNavigation';
import { QUESTION } from '../../constants/terminology';

const THROTTLE_DELAY = 500;

const fields: FieldProps[] = [
  {
    code: 'name',
    type: 'text',
    label: 'Name of custom report',
    placeholder: `For example: Gender diversity report for CSR ${(new Date()).getFullYear()}`,
    required: true,
  },
  {
    code: 'description',
    type: 'textarea',
    label: 'Description',
    placeholder: '',
  },
  {
    code: 'status',
    type: 'radio',
    label: 'Data I want to download:',
    placeholder: '',
    options: [
      {
        value: DownloadUtrvStatusCombined.All,
        label: `All ${QUESTION.PLURAL} (including unanswered)`,
      },
      {
        value: DownloadUtrvStatusCombined.AllAnswered,
        label: `All answered ${QUESTION.PLURAL} (answered and verified)`,
      },
      {
        value: UtrvStatus.Verified,
        label: `Verified ${QUESTION.PLURAL} only`,
      },
      {
        value: DownloadUtrvStatusCombined.Assured,
        label: `Assured ${QUESTION.PLURAL} only`
      },
    ]
  },
];

const getInitialSurveyState = (customReport?: CustomInitiatives): Partial<NewInitiativesOrSurvey['survey']> | undefined => {
  return customReport?.survey;
};

interface CustomReportEditFormSubsidiaryProps {
  customReport?: CustomInitiatives;
  type: CustomReportType;
  handleReload: () => Promise<void>;
}

const isFormValid = (formData?: Partial<FormData>, surveyData?: Partial<NewInitiativesOrSurvey['survey']>) => {
  if (!formData?.name || !surveyData?.initiativeIds || surveyData?.initiativeIds.length === 0 || !surveyData?.period || !surveyData?.effectiveDate) {
    return false;
  }
  return true;
}

const CustomReportEditFormSubsidiary = (props: CustomReportEditFormSubsidiaryProps) => {
  const { customReport } = props;

  const [formData, setFormData] = useState<Partial<FormData> | undefined>(getInitialState(customReport));
  const [surveyData, setSurveyData] = useState<Partial<NewInitiativesOrSurvey['survey']> | undefined>(getInitialSurveyState(customReport));
  const [isSaving, setSaving] = useState(false);
  const throttleTimeout = React.useRef<ReturnType<typeof setTimeout> | undefined>();
  const [isSurveyListDirty, setSurveyListDirty] = useState(false);

  const initiativeState = useAppSelector((state) => state.initiative);

  const history = useHistory();

  const setInitiativeIds = useCallback((initiativeIds: string[]) => {
    setSurveyData(state => ({
      ...state,
      initiativeIds
    }));
  }, [setSurveyData]);

  React.useEffect(() => setFormData(getInitialState(customReport)), [customReport]);

  const handleSelectedNode = useCallback((selectedNodes?: TableNodeWithSurveyIds[]) => {
    const selectedNode = selectedNodes ? [...selectedNodes].pop() : undefined;
    setSurveyData(state => ({
      ...state,
      period: selectedNode?.period,
      effectiveDate: selectedNode?.effectiveDate,
      type: selectedNode?.surveyType,
    }));
  }, []);

  if (!initiativeState.loaded) {
    return <Loader />;
  }

  const initiativeId = initiativeState.id;

  const handleUpdateInitiativeIds = (ids: string[]) => {
    setSurveyListDirty(true);

    if (throttleTimeout.current) {
      clearTimeout(throttleTimeout.current);
    }

    throttleTimeout.current = setTimeout(() => {
      setInitiativeIds(ids);
      setSurveyListDirty(false);
    }, THROTTLE_DELAY);
  }

  const handleUpdate = (e: React.ChangeEvent<{ name: string, value: string | number | undefined }>) => {
    const { name, value } = e.target;

    if (!fields.map(f => f.code).includes(name)) {
      return;
    }

    setFormData({
      ...(formData ?? {}),
      [name]: value
    });
  };

  const isEditMode = Boolean(formData?._id);

  const handleCancel = () => {
    const url = generateUrl(CustomReportRoutes.ROOT, { initiativeId });
    history.push(url);
  }

  const handleSubmit = async () => {
    if (!formData || !surveyData) {
      return;
    }

    setSaving(true);

    if (
      !formData.name ||
      !surveyData.initiativeIds ||
      surveyData.initiativeIds.length === 0 ||
      !surveyData.period ||
      !surveyData.effectiveDate
    ) {
      return;
    }

    const data: NewInitiativesOrSurvey = {
      type: CustomReportType.Initiatives,
      name: formData.name,
      description: formData.description,
      survey: {
        initiativeIds: surveyData.initiativeIds,
        period: surveyData.period,
        effectiveDate: surveyData.effectiveDate,
        type: surveyData.type,
        ...convertToUtrvStatuses(formData.status),
      }
    };

    const method = formData._id ? G17Client.updateCustomReport(initiativeId, formData._id, data) : G17Client.createCustomReport(initiativeId, data);
    method
      .then((data) => {
        const url = generateUrl(CustomReportRoutes.ROOT, { initiativeId });
        history.push(url);
        setSaving(false);
        setFormData(undefined);
        setSurveyData(undefined);
      })
      .catch(() => {
        setSaving(false);
      });
  }

  return <>
    <FormGenerator
      form={formData ?? {}}
      fields={fields.map(f => ({ ...f, disabled: isSaving }))}
      updateForm={handleUpdate}
    />

    <div className='mt-3'>
      <hr />

      <div className='mt-4 strong'>Which subsidiaries would you like to compare:</div>

      <div className='mt-3 downloads-table-container'>
        <SubsidiarySelectionTable initiativeId={initiativeId} selectedIds={customReport?.survey.initiativeIds} handleSetSelectedIds={handleUpdateInitiativeIds} />
      </div>
    </div>

    <div className='mt-5'>
      <hr />

      <div className='mt-4 strong'>Which survey would you like to compare (max 1):</div>

      <div className='mt-3 downloads-table-container'>
        <LoadingPlaceholder height={400} isLoading={isSurveyListDirty}>
          {surveyData?.initiativeIds && surveyData?.initiativeIds.length > 0
            ?
            <SurveySelectionTable
              initiativeId={initiativeId}
              initiativeIds={surveyData?.initiativeIds ?? []}
              handleSelectedNodes={handleSelectedNode}
              selectedNodeIds={getSelectedSurveyNodeIds(customReport?.survey ? [customReport.survey] : [])}
            />
            :
            <BasicAlert type={'warning'}>Please select subsidiaries above</BasicAlert>
          }
        </LoadingPlaceholder>
      </div>
    </div>
    <div className='mt-4 text-right'>
      <Button color='link-secondary' onClick={handleCancel} className='mr-4'>Cancel</Button>
      <SubmitButton
        disabled={!isFormValid(formData, surveyData) || isSaving}
        color='primary'
        onClick={handleSubmit}>
        {isEditMode ? 'Update' : 'Create'}
      </SubmitButton>
    </div>
  </>
}

export default CustomReportEditFormSubsidiary;
